import { GraphQL } from "@rpi/openapi-api";
import { buildLocalRefundPreview, type OrderForRefund, type RefundRequest } from "./refund.utils";

export type RefundPreviewData = GraphQL.CalculateRefundByQuantityQuery["calculateRefundByQuantity"];

export interface RefundPreviewResult {
  data: RefundPreviewData | undefined;
  isFetching: boolean;
  error: unknown;
}

export function useRefundPreview(
  customerPublicId: string,
  request: RefundRequest | undefined,
  order: Pick<OrderForRefund, "pricing">
): RefundPreviewResult {
  const enabledBase = !!customerPublicId && !!request;

  const byAmountInput: GraphQL.BraintreeRefundByAmountInput =
    request?.kind === "byAmount"
      ? request.input
      : { customerOrderId: "", amountItems: [], reason: GraphQL.RefundReason.Other };
  const byPercentageInput: GraphQL.BraintreeRefundByPercentageInput =
    request?.kind === "byPercentage"
      ? request.input
      : { customerOrderId: "", percentageItems: [], reason: GraphQL.RefundReason.Other };
  const byQuantityInput: GraphQL.BraintreeRefundByQuantityInput =
    request?.kind === "byQuantity"
      ? request.input
      : { customerOrderId: "", quantityItems: [], reason: GraphQL.RefundReason.Other };

  const byAmount = GraphQL.useCalculateRefundByAmountQuery(
    { customerPublicId, input: byAmountInput },
    { enabled: enabledBase && request?.kind === "byAmount", keepPreviousData: true }
  );
  const byPercentage = GraphQL.useCalculateRefundByPercentageQuery(
    { customerPublicId, input: byPercentageInput },
    { enabled: enabledBase && request?.kind === "byPercentage", keepPreviousData: true }
  );
  const byQuantity = GraphQL.useCalculateRefundByQuantityQuery(
    { customerPublicId, input: byQuantityInput },
    { enabled: enabledBase && request?.kind === "byQuantity", keepPreviousData: true }
  );

  if (!request) {
    return { data: undefined, isFetching: false, error: undefined };
  }

  // Full-refund and shipping-only have no backend preview endpoint —
  // synthesize from the already-fetched order pricing.
  const localPreview: RefundPreviewResult = {
    data: buildLocalRefundPreview(request, order),
    isFetching: false,
    error: undefined,
  };

  const previewByKind: Record<RefundRequest["kind"], RefundPreviewResult> = {
    byAmount: { data: byAmount.data?.calculateRefundByAmount, isFetching: byAmount.isFetching, error: byAmount.error },
    byPercentage: {
      data: byPercentage.data?.calculateRefundByPercentage,
      isFetching: byPercentage.isFetching,
      error: byPercentage.error,
    },
    byQuantity: {
      data: byQuantity.data?.calculateRefundByQuantity,
      isFetching: byQuantity.isFetching,
      error: byQuantity.error,
    },
    fullRefund: localPreview,
    shippingOnly: localPreview,
  };

  return previewByKind[request.kind];
}
